type TabType = 'partner' | 'mentor' | 'student';

interface Props {
  activeTab: TabType;
}

const channels = [
  {
    icon: 'ri-mail-line',
    label: '通用信箱',
    value: 'admin@nexusgo.ai',
    sub: '工作日 24 小時內回覆',
    color: 'bg-violet-50 text-violet-600',
  },
  {
    icon: 'ri-line-line',
    label: 'LINE 客服',
    value: '@nexusgo',
    sub: '加好友，即時回應',
    color: 'bg-emerald-50 text-emerald-600',
  },
];

const mentorBenefits = [
  { icon: 'ri-money-dollar-circle-line', text: '設定自己的諮詢費率，收入 100% 歸你' },
  { icon: 'ri-time-line', text: '彈性安排時間，每週投入 2–4 小時即可' },
  { icon: 'ri-group-line', text: '觸及 1,200+ 位認真求職的優質學員' },
  { icon: 'ri-medal-line', text: '平台品牌背書，提升個人專業影響力' },
  { icon: 'ri-book-open-line', text: '共同開發課程，額外創造被動收入' },
];

const partnerHighlights = [
  { icon: 'ri-building-4-line', text: '支援人才內訓、共創課程、品牌合作等多種形式' },
  { icon: 'ri-team-line', text: '可對接 500+ 位各領域頂尖導師資源' },
  { icon: 'ri-trophy-line', text: '已服務 50+ 家企業與機構合作夥伴' },
  { icon: 'ri-settings-line', text: '根據企業規模與需求，客製彈性合作方案' },
];

const studentFaqs = [
  { q: '付款後沒收到確認信怎麼辦？', a: '請先檢查垃圾郵件夾，如仍未收到請填寫左側表單並附上訂單號。' },
  { q: '如何申請退款？', a: '課程購買 7 天內、學習進度未超過 20% 可全額退款，填寫表單選「退款申請」即可。' },
  { q: '導師臨時取消了怎麼辦？', a: '導師取消後平台會全額退款，並贈送下次諮詢 10% 折扣券。' },
];

const ContactInfoSidebar = ({ activeTab }: Props) => (
  <div className="space-y-5">
    {/* Contact Channels — always visible */}
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <h3 className="text-sm font-bold text-[#0A1628] mb-4 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-contacts-line text-violet-500" />
        </div>
        聯絡方式
      </h3>
      <div className="space-y-3">
        {channels.map((c) => (
          <div key={c.label} className="flex items-start gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${c.color}`}>
              <i className={`${c.icon} text-base`} />
            </div>
            <div>
              <p className="text-[11px] text-slate-400 font-medium">{c.label}</p>
              <p className="text-sm font-semibold text-[#0A1628] leading-snug">{c.value}</p>
              <p className="text-[11px] text-slate-400">{c.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Mentor Tab — benefits */}
    {activeTab === 'mentor' && (
      <>
        <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-5 text-white">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-sparkling-line" />
            </div>
            成為 NexusGo 導師的好處
          </h3>
          <div className="space-y-3">
            {mentorBenefits.map((b) => (
              <div key={b.text} className="flex items-start gap-2.5">
                <div className="w-6 h-6 flex items-center justify-center bg-white/15 rounded-lg flex-shrink-0">
                  <i className={`${b.icon} text-white/90 text-xs`} />
                </div>
                <p className="text-xs text-white/85 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-[#0A1628] mb-1">已合作的導師來自</h3>
          <p className="text-xs text-slate-400 mb-4">全球頂尖科技與顧問公司</p>
          <div className="grid grid-cols-3 gap-2">
            {['Google', 'NVIDIA', 'Meta', 'Microsoft', 'McKinsey', 'Amazon'].map((co) => (
              <div key={co} className="bg-slate-50 rounded-xl py-2.5 px-3 text-center text-xs font-bold text-slate-500 border border-slate-100">
                {co}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-[#0A1628] mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-route-line text-violet-500" />
            </div>
            入駐流程
          </h3>
          <div className="space-y-3">
            {[
              { step: '01', title: '提交申請', desc: '填寫右側表單，3–5 個工作日內審核' },
              { step: '02', title: '資質審核', desc: '團隊核實背景與專業資質' },
              { step: '03', title: '簡介設定', desc: '完善個人 Profile 與服務套餐' },
              { step: '04', title: '開始接單', desc: '平台推薦曝光，學員主動找上門' },
            ].map((s) => (
              <div key={s.step} className="flex items-start gap-3">
                <span className="text-[11px] font-black text-violet-400 w-6 flex-shrink-0 pt-0.5">{s.step}</span>
                <div>
                  <p className="text-xs font-semibold text-[#0A1628]">{s.title}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    )}

    {/* Partner Tab — highlights */}
    {activeTab === 'partner' && (
      <>
        <div className="bg-gradient-to-br from-[#0A1628] to-slate-700 rounded-2xl p-5 text-white">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-handshake-line" />
            </div>
            為什麼選擇 NexusGo 合作
          </h3>
          <div className="space-y-3">
            {partnerHighlights.map((b) => (
              <div key={b.text} className="flex items-start gap-2.5">
                <div className="w-6 h-6 flex items-center justify-center bg-white/15 rounded-lg flex-shrink-0">
                  <i className={`${b.icon} text-white/90 text-xs`} />
                </div>
                <p className="text-xs text-white/85 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-[#0A1628] mb-1">已合作的企業來自</h3>
          <p className="text-xs text-slate-400 mb-4">各行業領先企業與機構</p>
          <div className="grid grid-cols-3 gap-2">
            {['台積電', '鴻海', '聯發科', '中華電信', '富邦金', '玉山銀行'].map((co) => (
              <div key={co} className="bg-slate-50 rounded-xl py-2.5 px-3 text-center text-xs font-bold text-slate-500 border border-slate-100">
                {co}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-[#0A1628] mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-route-line text-violet-500" />
            </div>
            合作流程
          </h3>
          <div className="space-y-3">
            {[
              { step: '01', title: '提交需求', desc: '填寫表單，說明合作方向與規模' },
              { step: '02', title: '商務對接', desc: '1–2 個工作日內，專人與你聯繫' },
              { step: '03', title: '方案客製', desc: '根據需求制定專屬合作方案' },
              { step: '04', title: '正式合作', desc: '簽約後，專案正式啟動推進' },
            ].map((s) => (
              <div key={s.step} className="flex items-start gap-3">
                <span className="text-[11px] font-black text-violet-400 w-6 flex-shrink-0 pt-0.5">{s.step}</span>
                <div>
                  <p className="text-xs font-semibold text-[#0A1628]">{s.title}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    )}

    {/* Student Tab — quick help */}
    {activeTab === 'student' && (
      <>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
          <h3 className="text-sm font-bold mb-1 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-customer-service-2-line" />
            </div>
            我們的回覆承諾
          </h3>
          <p className="text-xs text-white/75 mb-4">工作日內快速回應</p>
          <div className="space-y-2.5">
            {[
              { label: '一般諮詢', time: '1 個工作日內', icon: 'ri-chat-1-line' },
              { label: '退款申請', time: '2 個工作日內', icon: 'ri-refund-2-line' },
              { label: '緊急技術問題', time: '4 小時內（工作日）', icon: 'ri-tools-line' },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between bg-white/15 rounded-xl px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <i className={`${r.icon} text-white/80 text-sm`} />
                  <span className="text-xs text-white/90">{r.label}</span>
                </div>
                <span className="text-[11px] font-bold text-white">{r.time}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="text-sm font-bold text-[#0A1628] mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-question-answer-line text-violet-500" />
            </div>
            學員常見問題
          </h3>
          <div className="space-y-4">
            {studentFaqs.map((faq) => (
              <div key={faq.q}>
                <p className="text-xs font-semibold text-[#0A1628] mb-1">{faq.q}</p>
                <p className="text-[11px] text-slate-400 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </>
    )}
  </div>
);

export default ContactInfoSidebar;
